import { useEffect, useRef, useState } from 'react';
import './MissionReportPage.sass';
import moment from 'moment';
import { formatDuration } from 'src/helper/utils';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { getDeviceReportingToken, getPublicReportingToken, setDeviceReportingToken, setPublicReportingTokens } from '../../ReportingAppSlice';
import Tabs from 'src/hci/common/Tabs/Tabs';
import { TbArrowLeft, TbGraph, TbLibraryPhoto, TbWorld, TbWorldOff } from 'react-icons/tb';
import DeviceMediaList from '../../../ExploreApp/OperationDashboard/common/DeviceMediaList/DeviceMediaList';
import useDeviceInfo from 'src/services/device/common/useDeviceInfo';
import Badge from 'src/hci/common/Badge/Badge';
import useDeviceCapturedMedia from 'src/services/device/common/useDeviceCapturedMedia';
import { useHistory, useParams } from 'react-router-dom';
import MissionMediaSlide from './MissionMediaSlide/MissionMediaSlide';
import Avatar from 'src/hci/common/Avatar/Avatar';
import Loading from 'src/hci/common/Loading/Loading';
import Button from 'src/hci/common/Button/Button';
import { setMissionPublic } from 'src/services/mission/MissionServiceSlice';
import useMeasurement from 'src/helper/useMeasurement';
import { MeasurementSystem } from 'src/helper/constants';

const MissionReportPanelType = {
  MAP: 3,
  TELEMETRIES: 1,
  BATTERY: 2,
  HMS: 4,
  NETWORK: 5,
  BATTERY_IMPERIAL: 6,
  TELEMETRIES_IMPERIAL: 7,
}

const MissionReportTab = {
  INFO: 'info',
  MEDIA: 'media',
}

function MissionReportPage({ mission, isPublic = false }) {
  const dispatch = useDispatch();
  const store = useStore();
  const reportToken = useSelector(state => isPublic ? state.reportingApp.publicReportingTokens[mission?.dock?.id] : state.reportingApp.deviceReportingTokens[mission?.dock?.id]);
  const deviceInfo = useDeviceInfo(mission?.dock?.id);
  const mediaPath = deviceInfo?.serialNumber + '/' + (mission?.flightDetails?.id || mission?.flightDetails?.Id);
  const capturedMedia = useDeviceCapturedMedia(mission?.dock?.id, mediaPath, true);
  const capturedImages = Array.isArray(capturedMedia?.mediaList) ? capturedMedia.mediaList.filter(file => ['jpg', 'jpeg'].includes(file.nameInfo?.extension)) : null;
  const capturedVideos = Array.isArray(capturedMedia?.mediaList) ? capturedMedia.mediaList.filter(file => ['mp4'].includes(file.nameInfo?.extension)) : null;
  const { deviceId } = useParams();
  const history = useHistory();
  const { measurementSystem } = useMeasurement();
  const [reportCurrTab, setReportCurrTab] = useState(MissionReportTab.INFO);
  const [missionPublishLoading, setMissionPublishLoading] = useState(false);
  const [missionPublishStatus, setMissionPublishStatus] = useState({});
  const publishResult = useSelector(state => state.missionService.setMissionPublicResult);

  const getReportToken = () => {
    if(isPublic) {
      dispatch(getPublicReportingToken({
        deviceId: mission.dock?.id,
        apiKey: new URLSearchParams(window.location.search).get('x-api-key'),
      }));
    } else {
      dispatch(getDeviceReportingToken({
        deviceId: mission.dock?.id,
        timezone: 'browser',
      }));
    }
  };

  const getPanelUrl = (panelId) => {
    if(!reportToken) return;
    return reportToken?.url + '&panelId=' + panelId + '&from=' + mission.startTime + '&to=' + mission.endTime;
  }

  const handleReturnClick = () => {
    if(isPublic) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete('missionId');
      history.push(`/public-dashboard/?${searchParams.toString()}`);
    } else {
      history.push(`/dashboard/reporting-app/missions/${deviceId}`);
    }
  }

  const handlePublishMissionClick = () => {
    const newPublicStatus = !missionPublishStatus[mission?.id];
    setMissionPublishLoading(true);
    dispatch(setMissionPublic(mission?.id, newPublicStatus));
  }

  const panels = {
    map: getPanelUrl(MissionReportPanelType.MAP),
    telemetries: getPanelUrl(MissionReportPanelType.TELEMETRIES),
    battery: getPanelUrl(MissionReportPanelType.BATTERY),
    network: getPanelUrl(MissionReportPanelType.NETWORK),
    hms: getPanelUrl(MissionReportPanelType.HMS),
    batteryImperial: getPanelUrl(MissionReportPanelType.BATTERY_IMPERIAL),
    telemetriesImperial: getPanelUrl(MissionReportPanelType.TELEMETRIES_IMPERIAL),
  }

  useEffect(() => {
    if(!publishResult) return;

    if(publishResult?.meta?.missionId === mission?.id) {
      setMissionPublishLoading(false);
      setMissionPublishStatus(curr => ({
        ...curr,
        [mission?.id]: publishResult?.meta?.isPublic
      }));
    }
  }, [publishResult]);

  useEffect(() => {
    if(!mission) return;

    setMissionPublishStatus(curr => ({
      ...curr,
      [mission?.id]: curr[mission?.id] === undefined ? mission?.published : curr[mission?.id],
    }));


  }, [mission?.id]);

  useEffect(() => {
    if(!mission) return;

    const currDeviceToken = isPublic ? 
      store.getState().reportingApp.publicReportingTokens[mission?.dock?.id] : 
      store.getState().reportingApp.deviceReportingTokens[mission?.dock?.id];

    if(!currDeviceToken) {
      getReportToken();
    }
  }, [mission, reportToken, isPublic]);

  return (
    <div className="mission-report-page">
      <div className="page-header">
        <TbArrowLeft className="return" onClick={handleReturnClick} />
        <div className="main">
          <div className="title">{ mission?.name }</div>
          <div className="info">
            <div className="description">{moment(mission?.startTime).format('MMM DD, YYYY HH:mm:ss')}</div>
            <div className="tags">
              <Badge color="green" variant="outline">{ mission?.status?.toUpperCase() }</Badge>
            </div>
          </div>
        </div>
        <div className="tools">
          {!isPublic && (
            <Button onClick={handlePublishMissionClick} loading={missionPublishLoading} prefix={!missionPublishStatus[mission?.id] ? <TbWorld /> : <TbWorldOff />}>
              {missionPublishLoading ? 'Applying...' : missionPublishStatus[mission?.id] ? 'Unpublish' : 'Publish'}
            </Button>
          )}
        </div>
      </div>
      <div className="content">
        <Tabs.Root value={reportCurrTab} onValueChange={setReportCurrTab} style={{ height: '100%' }}>
          <Tabs.List size="2" className="mission-tab-list">
            <Tabs.Trigger value={MissionReportTab.INFO}>
              <TbGraph className="icon" /> 
              <span>Mission Details</span>
            </Tabs.Trigger>
            {!isPublic && (
              <Tabs.Trigger value={MissionReportTab.MEDIA}>
                <TbLibraryPhoto className="icon" />
                <span>Captured Media</span>
              </Tabs.Trigger>
            )}
          </Tabs.List>
          <div style={{ flexGrow: '1' }}>
            <Tabs.Content value={MissionReportTab.INFO}>
              <div className="details-tab-content">
                <div className="left-panels">
                  <div className="info-panel panel">
                    <div className="panel-title">Summary</div>
                    <div className="panel-content">
                      <div className="info-item">
                        <div className="title">Takeoff Time</div>
                        <div className="value">{ moment(mission?.startTime).format('MMM DD, YYYY HH:mm:ss') }</div>
                      </div>
                      <div className="info-item">
                        <div className="title">Landing Time</div>
                        <div className="value">{ moment(mission?.endTime).format('MMM DD, YYYY HH:mm:ss') }</div>
                      </div>
                      <div className="info-item">
                        <div className="title">Duration</div>
                        <div className="value">{ mission?.endTime ? formatDuration(mission.endTime - mission.startTime) : 'N/A' }</div>
                      </div>
                      <div className="info-item">
                        <div className="title">Mission ID</div>
                        <div className="value">{ mission?.id }</div>
                      </div>
                      <div className="info-item">
                        <div className="title">Flight ID</div>
                        <div className="value">{ mission?.flightDetails?.Id }</div>
                      </div>
                    </div>
                  </div>
                  {!isPublic && <>
                    <div className="info-panel panel">
                      <div className="panel-title">Device Info</div>
                      <div className="panel-content">
                        <div className="info-item">
                          <div className="title">Name</div>
                          <div className="value">{ mission?.dock?.name }</div>
                        </div>
                        <div className="info-item">
                          <div className="title">Device SN</div>
                          <div className="value">{ mission?.dock?.serialNumber }</div>
                        </div>
                        <div className="info-item">
                          <div className="title">Device Model</div>
                          <div className="value">N/A</div>
                        </div>
                        <div className="info-item">
                          <div className="title">Device ID</div>
                          <div className="value">{ mission?.dock?.id }</div>
                        </div>
                      </div>
                    </div>
                    <div className="info-panel panel">
                      <div className="panel-title">Mission Owner</div>
                      <div className="panel-content">
                        <Avatar image={mission?.createdBy?.avatar} size="70px" className="owner-avatar" />
                        <div className="info-item">
                          <div className="title">Name</div>
                          <div className="value">{ mission?.createdBy?.displayName }</div>
                        </div>
                        <div className="info-item">
                          <div className="title">Email</div>
                          <div className="value">{ mission?.createdBy?.emailAddress }</div>
                        </div>
                      </div>
                    </div>
                  </>}
                </div>
                <div className="right-panels">
                  <div className='graph-panel panel'>
                    <div className="panel-title">Map</div>
                    <div className="panel-content">
                      <iframe src={panels.map} />
                    </div>
                  </div>
                  {(!!capturedImages?.length || !!capturedVideos?.length) && (
                    <div className="media-panels">
                      {!isPublic && capturedImages?.length > 0 && (
                        <div className="panel media-panel">
                          <div className="panel-title">Captured Images</div>
                          <div className="panel-content">
                            <MissionMediaSlide mediaList={capturedImages} />
                          </div>
                        </div>
                      )}
                      {!isPublic && capturedVideos?.length > 0 && (
                        <div className="panel media-panel">
                          <div className="panel-title">Captured Videos</div>
                          <div className="panel-content">
                            <MissionMediaSlide mediaList={capturedVideos} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className='graph-panel panel'>
                    <div className="panel-title">Device Metrics</div>
                    <div className="panel-content">
                      <iframe src={measurementSystem === MeasurementSystem.METRIC ? panels.telemetries : panels.telemetriesImperial} />
                    </div>
                  </div>
                  <div className='graph-panel panel'>
                    <div className="panel-title">Battery</div>
                    <div className="panel-content">
                      <iframe src={measurementSystem === MeasurementSystem.METRIC ? panels.battery : panels.batteryImperial} />
                    </div>
                  </div>
                  <div className='graph-panel panel'>
                    <div className="panel-title">Network</div>
                    <div className="panel-content">
                      <iframe src={panels.network} />
                    </div>
                  </div>
                  <div className='graph-panel panel'>
                    <div className="panel-title">HMS</div>
                    <div className="panel-content">
                      <iframe src={panels.hms} />
                    </div>
                  </div>
                </div>
              </div>
            </Tabs.Content>
            {!isPublic && (
              <Tabs.Content value={MissionReportTab.MEDIA} style={{ position: 'relative', height: '100%' }}>
                <div className="media-tab-content">
                  <DeviceMediaList
                    deviceId={mission?.dock?.id}
                    path={mediaPath}
                    defaultGroupBy={false}
                    style={{ height: '100%' }}
                  />
                </div>
              </Tabs.Content>
            )}
          </div>
        </Tabs.Root>
      </div>
      {(!mission || (!isPublic && !capturedMedia?.mediaList)) && <Loading message="Loading mission report..." />}
    </div>
  )
}

export default MissionReportPage